import React from "react";
import { Link } from "react-router-dom";
import { PilotButton } from "../components/PilotIntake";

function Eyebrow({ children }) {
  return (
    <span
      className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] px-3 py-1.5 rounded-full"
      style={{
        color: "var(--primary-color)",
        backgroundColor: "var(--primary-50)",
        border: "1px solid color-mix(in srgb, var(--primary-color) 14%, transparent)",
      }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--primary-color)" }} />
      {children}
    </span>
  );
}

const PILLARS = [
  {
    n: "01",
    title: "Live polling",
    body: "Multiple choice, free response, or correct-answer polls, launched in two clicks. Students join with a code, with no app install needed.",
  },
  {
    n: "02",
    title: "Student Q&A",
    body: "An upvoted question feed surfaces what the room actually needs. Address real confusion in real time.",
  },
  {
    n: "03",
    title: "Participation tracking",
    body: "Automatic attendance and response logging across every session, every week, and every semester.",
  },
  {
    n: "04",
    title: "Session analytics",
    body: "Per-class dashboards reveal accuracy, response time, and stuck concepts. Drill into any student.",
  },
  {
    n: "05",
    title: "LMS grade sync",
    body: "One-click sync of participation grades to your LMS gradebook. No CSVs. No mistakes.",
  },
  {
    n: "06",
    title: "AI class summaries",
    body: "The moment class ends, get an AI brief of what landed, what didn't, and what to revisit next.",
  },
];

const WORKFLOW = [
  { k: "Plan", v: "Build your class roster and LMS link in minutes." },
  { k: "Run", v: "Launch live polls and Q&A, with students responding on any device." },
  { k: "Review", v: "Read an AI-generated brief the second the session ends." },
  { k: "Sync", v: "Push participation grades to your LMS with a single click." },
];

export default function ProductDetail() {
  return (
    <main style={{ backgroundColor: "var(--bg-color)", color: "var(--text-color)" }}>
      {/* Hero */}
      <section className="relative pt-16 pb-20 sm:pt-24 sm:pb-24 overflow-hidden" style={{ background: "var(--gradient-soft)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <Eyebrow>The product</Eyebrow>
            <h1 className="mt-6 text-4xl sm:text-6xl font-semibold tracking-tight" style={{ color: "var(--text-color)" }}>
              One quiet tool for{" "}
              <span className="ce-serif italic" style={{ color: "var(--accent-color)" }}>every part of class.</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed max-w-2xl" style={{ color: "var(--muted-color)" }}>
              CoursEngage replaces your clicker, your Q&amp;A board, your attendance sheet, and your post-class analysis with one interface designed for university teaching.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-start gap-3">
              <PilotButton className="ce-btn ce-btn-primary text-base px-6 py-3.5">Join the pilot</PilotButton>
              <Link to="/why-choose-us" className="ce-btn ce-btn-ghost text-base px-6 py-3.5">Why CoursEngage →</Link>
            </div>
          </div>

          {/* Product mockup card */}
          <div
            className="mt-16 mx-auto max-w-5xl rounded-2xl overflow-hidden"
            style={{ backgroundColor: "var(--card-bg)", border: "1px solid var(--border-color)", boxShadow: "var(--shadow-lg)" }}
          >
            <div className="aspect-video w-full">
              <iframe
                src="https://www.youtube.com/embed/L1MjtwABHC4?si=87IjM3MMVTZ1sLJD"
                title="CoursEngage product overview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pillars grid */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <Eyebrow>What's inside</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--text-color)" }}>
              Six pillars. <span className="ce-serif italic" style={{ color: "var(--accent-color)" }}>One platform.</span>
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px rounded-2xl overflow-hidden"
            style={{ backgroundColor: "var(--border-color)", border: "1px solid var(--border-color)" }}>
            {PILLARS.map((p) => (
              <div key={p.n} className="p-7 sm:p-8" style={{ backgroundColor: "var(--card-bg)" }}>
                <div className="ce-serif text-2xl mb-3" style={{ color: "var(--primary-color)" }}>{p.n}</div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: "var(--text-color)" }}>{p.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--muted-color)" }}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow band */}
      <section className="py-20 sm:py-24" style={{ backgroundColor: "var(--alt-bg)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 items-center">
          <div>
            <Eyebrow>How it works</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--text-color)" }}>
              Plan, run, review,{" "}
              <span className="ce-serif italic" style={{ color: "var(--accent-color)" }}>sync.</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed max-w-md" style={{ color: "var(--muted-color)" }}>
              A teaching workflow that disappears into the background, so the room stays focused on the lecture.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {WORKFLOW.map((s, i) => (
              <div key={s.k} className="rounded-xl p-6" style={{ backgroundColor: "var(--card-bg)", border: "1px solid var(--border-color)" }}>
                <div className="text-xs uppercase tracking-[0.14em] font-semibold mb-2" style={{ color: "var(--muted-color)" }}>
                  Step 0{i + 1}
                </div>
                <div className="text-lg font-semibold mb-1.5" style={{ color: "var(--text-color)" }}>{s.k}</div>
                <p className="text-sm leading-relaxed" style={{ color: "var(--muted-color)" }}>{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture / trust */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <Eyebrow>Built to be trusted</Eyebrow>
          <h2 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl mx-auto" style={{ color: "var(--text-color)" }}>
            Modern infrastructure. <span className="ce-serif italic" style={{ color: "var(--accent-color)" }}>Privacy-first.</span>
          </h2>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { k: "LTI", v: "Native LMS integration" },
              { k: "Roster", v: "Simple campus-ready setup" },
              { k: "Privacy", v: "Student data handled with care" },
              { k: "99.9%", v: "Session uptime, every term" },
            ].map((s, i) => (
              <div key={s.k} className={`text-left px-2 ${i > 0 ? "md:border-l md:pl-6" : ""}`} style={{ borderColor: "var(--border-color)" }}>
                <div className="ce-serif text-3xl sm:text-4xl" style={{ color: "var(--primary-color)" }}>{s.k}</div>
                <div className="mt-2 text-sm" style={{ color: "var(--muted-color)" }}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div
            className="rounded-3xl p-12 sm:p-20 text-center"
            style={{ backgroundColor: "var(--alt-bg)", border: "1px solid var(--border-color)" }}
          >
            <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight" style={{ color: "var(--text-color)" }}>
              See it in your <span className="ce-serif italic" style={{ color: "var(--accent-color)" }}>next class.</span>
            </h2>
            <p className="mt-5 text-base sm:text-lg max-w-xl mx-auto" style={{ color: "var(--muted-color)" }}>
              Complimentary during the limited pilot. Five-minute setup. No card required.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
              <PilotButton className="ce-btn ce-btn-primary text-base px-6 py-3.5">Apply for pilot</PilotButton>
              <Link to="/contact" className="ce-btn ce-btn-ghost text-base px-6 py-3.5">Talk to our team →</Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
